/*
 * Copyright 2026 Sony Corporation
 */
import { liveSteps, settledSteps, type Roadmap, type RoadmapStage, type RoadmapStep } from '@ips-jit/schema';

export type StepIcon = 'done' | 'active' | 'pending' | 'failed' | 'skipped' | 'removed';

/** The stages a running step goes through, in order, as the panel names them. */
export const STAGES: { id: RoadmapStage; label: string }[] = [
  { id: 'writing', label: 'Write' },
  { id: 'building', label: 'Build & review' },
  { id: 'deploying', label: 'Send' },
  { id: 'checking', label: 'Check' },
];

/** m:ss */
export const clock = (ms: number): string => {
  const total = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

export const iconOf = (step: RoadmapStep): StepIcon =>
  ({
    planned: 'pending',
    running: 'active',
    done: 'done',
    failed: 'failed',
    skipped: 'skipped',
    removed: 'removed',
  })[step.state] as StepIcon;

/**
 * The steps the panel lists: those that count, and the ones the newest revision took out, shown once more crossed out
 * so the user sees what went away.
 */
export const shownSteps = (roadmap: Roadmap): RoadmapStep[] =>
  roadmap.steps.filter((s) => s.state !== 'removed' || s.revised_in === roadmap.revision);

/** How long a step took, or has been running. */
export const elapsedOf = (step: RoadmapStep, now: number): number | undefined => {
  if (!step.started_at) {
    return undefined;
  }
  const end = step.finished_at ? Date.parse(step.finished_at) : now;
  return end - Date.parse(step.started_at);
};

/** The line under a step's title: its version and time, how far it is, or what it is expected to take. */
export const detailOf = (step: RoadmapStep, now: number): string => {
  const elapsed = elapsedOf(step, now);
  switch (step.state) {
    case 'done':
      return [step.version ? `v${step.version}` : 'no change', elapsed !== undefined ? clock(elapsed) : undefined]
        .filter(Boolean)
        .join(' · ');
    case 'running':
      return `${clock(elapsed ?? 0)} / about ${clock(step.estimate_s * 1000)}`;
    case 'failed':
      return step.error ?? 'It did not reach the device.';
    case 'skipped':
      return 'Skipped';
    case 'removed':
      return 'Removed';
    default:
      return `about ${clock(step.estimate_s * 1000)}`;
  }
};

/** Seconds the steps not done yet are expected to take; a running step counts what is left of its estimate. */
export const remainingOf = (roadmap: Roadmap, now: number): number =>
  liveSteps(roadmap).reduce((sum, step) => {
    if (step.state === 'planned') {
      return sum + step.estimate_s;
    }
    if (step.state === 'running') {
      return sum + Math.max(0, step.estimate_s - (elapsedOf(step, now) ?? 0) / 1000);
    }
    return sum;
  }, 0);

/** "2/5": steps settled of those that count. */
export const progressOf = (roadmap: Roadmap): string => `${settledSteps(roadmap)}/${liveSteps(roadmap).length}`;

/** What the roadmap as a whole is doing, in a line. */
export const statusOf = (roadmap: Roadmap): string | undefined => {
  switch (roadmap.status) {
    case 'awaiting_approval':
      return 'Proposed. Choose a design and start it in the chat, or write what to change.';
    case 'paused':
      return roadmap.note ? `Paused. ${roadmap.note}` : 'Paused.';
    case 'done':
      return 'Done.';
    case 'cancelled':
      return 'Replaced or rejected.';
    default:
      return undefined;
  }
};
